/**
 * Pack list management storage service
 *
 * Handles pack list templates, trip-specific pack lists, item management,
 * weight calculations, and assignment tracking with offline-first patterns.
 */

import { db } from '../database/schema.js'
import type { PackList, PackItem } from '../database/schema.js'
import {
  OfflineFirstStorageService,
  type StorageResult,
  type QueryOptions,
} from './base-storage.js'
import { validatePackList, validatePackItem, runValidation } from './validation.js'
import { type UserPreferences } from '../lib/coordinates.js'

/**
 * Pack list creation data
 */
export interface CreatePackListData {
  name: string
  description: string
  category: string
  isTemplate: boolean
  createdBy: string
  isPublic: boolean
}

/**
 * Pack item creation data
 */
export interface CreatePackItemData {
  packListId: string
  name: string
  description?: string | undefined
  weight?: number | undefined
  category: string
  isEssential: boolean
  quantity: number
  assignedTo?: string | undefined
}

/**
 * Pack list with calculated metrics
 */
export interface PackListWithMetrics extends PackList {
  items?: PackItemWithMetrics[] | undefined
  totalItems?: number | undefined
  totalWeight?: number | undefined
  packedWeight?: number | undefined
  unpackedWeight?: number | undefined
  packedItems?: number | undefined
  unpackedItems?: number | undefined
  completionPercentage?: number | undefined
  assignedMembers?: string[] | undefined
  categoryBreakdown?: CategoryBreakdown[] | undefined
}

/**
 * Pack item with formatted weight and assignment details
 */
export interface PackItemWithMetrics extends PackItem {
  formattedWeight?: string | undefined
  assignedUser?:
    | {
        id: string
        displayName: string
        email: string
      }
    | undefined
}

/**
 * Category breakdown for weight analysis
 */
export interface CategoryBreakdown {
  category: string
  totalWeight: number
  packedWeight: number
  itemCount: number
  packedCount: number
  items: PackItem[]
}

/**
 * Pack list filters
 */
export interface PackListFilters {
  category?: string
  isTemplate?: boolean
  isPublic?: boolean
  createdBy?: string
}

/**
 * Pack item filters
 */
export interface PackItemFilters {
  category?: string
  isEssential?: boolean
  packed?: boolean
  assignedTo?: string
  weightRange?: {
    min?: number
    max?: number
  }
}

/**
 * Template application options
 */
export interface TemplateApplicationOptions {
  overwriteExisting?: boolean
  preserveAssignments?: boolean
  preservePackedStatus?: boolean
  categoryFilter?: string[]
}

/**
 * Pack list storage service
 */
export class PackListStorageService extends OfflineFirstStorageService<PackList> {
  constructor() {
    super(db.packLists, 'packList')
  }

  /**
   * Create a new pack list with validation
   */
  async createPackList(data: CreatePackListData): Promise<StorageResult<PackList>> {
    const validation = runValidation(validatePackList, data, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.create(data)
  }

  /**
   * Update pack list with validation
   */
  async updatePackList(id: string, updates: Partial<PackList>): Promise<StorageResult<PackList>> {
    const validation = runValidation(validatePackList, updates, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.update(id, updates)
  }

  /**
   * Get pack list with calculated metrics
   */
  async getPackListWithMetrics(
    packListId: string,
    preferences?: UserPreferences
  ): Promise<StorageResult<PackListWithMetrics | undefined>> {
    return this.executeOperation(async () => {
      const packList = await this.table.get(packListId)
      if (!packList) {
        return undefined
      }

      const items = await db.packItems.where('packListId').equals(packListId).toArray()

      // Calculate metrics
      const totalItems = items.length
      const packedItems = items.filter(item => item.packed).length
      const totalWeight = items.reduce((sum, item) => sum + (item.weight ?? 0) * item.quantity, 0)
      const packedWeight = items
        .filter(item => item.packed)
        .reduce((sum, item) => sum + (item.weight ?? 0) * item.quantity, 0)

      const completionPercentage = totalItems > 0 ? (packedItems / totalItems) * 100 : 0
      const assignedMembers = [
        ...new Set(items.map(item => item.assignedTo).filter(Boolean)),
      ] as string[]

      // Calculate category breakdown
      const categoryBreakdown = this.calculateCategoryBreakdown(items)

      // Format items if preferences provided
      let formattedItems: PackItemWithMetrics[] | undefined
      if (preferences) {
        // Get assigned users for formatting
        const userIds = items.map(item => item.assignedTo).filter(Boolean) as string[]
        const users = userIds.length > 0 ? await db.users.where('id').anyOf(userIds).toArray() : []

        formattedItems = items.map(item => ({
          ...item,
          formattedWeight: item.weight ? this.formatWeight(item.weight, preferences) : undefined,
          assignedUser: item.assignedTo ? users.find(u => u.id === item.assignedTo) : undefined,
        }))
      }

      const packListWithMetrics: PackListWithMetrics = {
        ...packList,
        items: formattedItems,
        totalItems,
        totalWeight,
        packedWeight,
        unpackedWeight: totalWeight - packedWeight,
        packedItems,
        unpackedItems: totalItems - packedItems,
        completionPercentage,
        assignedMembers,
        categoryBreakdown,
      }

      return packListWithMetrics
    }, 'get pack list with metrics')
  }

  /**
   * Get pack lists for user with filters
   */
  async getPackListsForUser(
    userId: string,
    filters: PackListFilters = {},
    options: QueryOptions = {}
  ): Promise<StorageResult<PackList[]>> {
    return this.executeOperation(async () => {
      let query = this.table.toCollection()

      // Filter by creator or public lists
      query = query.filter(packList => packList.createdBy === userId || packList.isPublic)

      // Apply filters
      if (filters.category) {
        query = query.filter(packList => packList.category === filters.category)
      }

      if (filters.isTemplate !== undefined) {
        query = query.filter(packList => packList.isTemplate === filters.isTemplate)
      }

      if (filters.isPublic !== undefined) {
        query = query.filter(packList => packList.isPublic === filters.isPublic)
      }

      if (filters.createdBy) {
        query = query.filter(packList => packList.createdBy === filters.createdBy)
      }

      let packLists = await query.toArray()

      // Apply sorting
      if (options.orderBy) {
        packLists.sort((a, b) => {
          const aVal = a[options.orderBy! as keyof PackList]
          const bVal = b[options.orderBy! as keyof PackList]
          const result = aVal > bVal ? 1 : aVal < bVal ? -1 : 0
          return options.orderDirection === 'desc' ? -result : result
        })
      }

      // Apply pagination
      if (options.offset) {
        packLists = packLists.slice(options.offset)
      }
      if (options.limit) {
        packLists = packLists.slice(0, options.limit)
      }

      return packLists
    }, 'get pack lists for user')
  }

  /**
   * Get public pack list templates
   */
  async getPublicTemplates(
    category?: string,
    options: QueryOptions = {}
  ): Promise<StorageResult<PackList[]>> {
    return this.executeOperation(async () => {
      let query = this.table.filter(packList => packList.isTemplate && packList.isPublic)

      if (category) {
        query = query.and(packList => packList.category === category)
      }

      let templates = await query.toArray()

      // Apply limit if specified
      if (options.limit) {
        templates = templates.slice(0, options.limit)
      }

      return templates
    }, 'get public templates')
  }

  /**
   * Search pack lists by name or description
   */
  async searchPackLists(
    query: string,
    userId: string,
    options: QueryOptions = {}
  ): Promise<StorageResult<PackList[]>> {
    return this.executeOperation(async () => {
      const searchTerm = query.toLowerCase()

      let packLists = await this.table
        .toCollection()
        .filter(
          packList =>
            (packList.createdBy === userId || packList.isPublic) &&
            (packList.name.toLowerCase().includes(searchTerm) ||
              packList.description.toLowerCase().includes(searchTerm))
        )
        .toArray()

      // Apply limit if specified
      if (options.limit) {
        packLists = packLists.slice(0, options.limit)
      }

      return packLists
    }, 'search pack lists')
  }

  /**
   * Duplicate pack list (template to trip-specific or create copy)
   */
  async duplicatePackList(
    sourceId: string,
    newName: string,
    userId: string,
    isTemplate = false
  ): Promise<StorageResult<PackList>> {
    return this.executeOperation(async () => {
      const source = await this.table.get(sourceId)
      if (!source) {
        throw new Error('Source pack list not found')
      }

      // Create new pack list
      const newPackList: CreatePackListData = {
        name: newName,
        description: `Copy of ${source.name}`,
        category: source.category,
        isTemplate,
        createdBy: userId,
        isPublic: false,
      }

      const createResult = await this.createPackList(newPackList)
      if (!createResult.success || !createResult.data) {
        throw new Error('Failed to create pack list copy')
      }

      // Copy items
      const sourceItems = await db.packItems.where('packListId').equals(sourceId).toArray()

      for (const item of sourceItems) {
        const newItem: CreatePackItemData = {
          packListId: createResult.data.id,
          name: item.name,
          description: item.description,
          weight: item.weight,
          category: item.category,
          isEssential: item.isEssential,
          quantity: item.quantity,
          // Don't copy assignments when duplicating
          assignedTo: undefined,
        }

        await db.packItems.add({
          ...newItem,
          id: crypto.randomUUID(),
          packed: false, // Reset packed status
        })
      }

      return createResult.data
    }, 'duplicate pack list')
  }

  /**
   * Get pack list categories
   */
  async getCategories(): Promise<StorageResult<string[]>> {
    return this.executeOperation(async () => {
      const packLists = await this.table.toArray()
      const categories = [...new Set(packLists.map(pl => pl.category))]
      return categories.sort()
    }, 'get pack list categories')
  }

  /**
   * Calculate category breakdown for items
   */
  private calculateCategoryBreakdown(items: PackItem[]): CategoryBreakdown[] {
    const categoryMap = new Map<string, CategoryBreakdown>()

    items.forEach(item => {
      const category = item.category
      const weight = (item.weight ?? 0) * item.quantity

      if (!categoryMap.has(category)) {
        categoryMap.set(category, {
          category,
          totalWeight: 0,
          packedWeight: 0,
          itemCount: 0,
          packedCount: 0,
          items: [],
        })
      }

      const breakdown = categoryMap.get(category)!
      breakdown.totalWeight += weight
      breakdown.itemCount += 1
      breakdown.items.push(item)

      if (item.packed) {
        breakdown.packedWeight += weight
        breakdown.packedCount += 1
      }
    })

    return Array.from(categoryMap.values()).sort((a, b) => a.category.localeCompare(b.category))
  }

  /**
   * Format weight for display
   */
  private formatWeight(grams: number, preferences: UserPreferences): string {
    if (preferences.unitSystem === 'imperial') {
      if (grams < 28.35) {
        return `${grams.toFixed(1)} g`
      }
      const ounces = grams / 28.35
      if (ounces < 16) {
        return `${ounces.toFixed(1)} oz`
      }
      const pounds = ounces / 16
      return `${pounds.toFixed(1)} lbs`
    }

    // Metric
    if (grams < 1000) {
      return `${grams.toFixed(0)} g`
    }
    const kg = grams / 1000
    return `${kg.toFixed(1)} kg`
  }
}

/**
 * Pack item storage service
 */
export class PackItemStorageService extends OfflineFirstStorageService<PackItem> {
  constructor() {
    super(db.packItems, 'packItem')
  }

  /**
   * Create a new pack item with validation
   */
  async createPackItem(data: CreatePackItemData): Promise<StorageResult<PackItem>> {
    const validation = runValidation(validatePackItem, data, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.create(data as Omit<PackItem, 'id' | 'createdAt' | 'updatedAt'>)
  }

  /**
   * Update pack item with validation
   */
  async updatePackItem(id: string, updates: Partial<PackItem>): Promise<StorageResult<PackItem>> {
    const validation = runValidation(validatePackItem, updates, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.update(id, updates)
  }

  /**
   * Get items for pack list with filters
   */
  async getItemsForPackList(
    packListId: string,
    filters: PackItemFilters = {}
  ): Promise<StorageResult<PackItem[]>> {
    return this.executeOperation(async () => {
      const query = this.table.where('packListId').equals(packListId)

      let items = await query.toArray()

      // Apply filters
      if (filters.category) {
        items = items.filter(item => item.category === filters.category)
      }

      if (filters.isEssential !== undefined) {
        items = items.filter(item => item.isEssential === filters.isEssential)
      }

      if (filters.packed !== undefined) {
        items = items.filter(item => item.packed === filters.packed)
      }

      if (filters.assignedTo) {
        items = items.filter(item => item.assignedTo === filters.assignedTo)
      }

      if (filters.weightRange) {
        items = items.filter(item => {
          if (!item.weight) {
            return !filters.weightRange?.min
          }
          if (filters.weightRange?.min && item.weight < filters.weightRange.min) {
            return false
          }
          if (filters.weightRange?.max && item.weight > filters.weightRange.max) {
            return false
          }
          return true
        })
      }

      return items
    }, 'get items for pack list')
  }

  /**
   * Toggle item packed status
   */
  async toggleItemPacked(itemId: string): Promise<StorageResult<PackItem>> {
    return this.executeOperation(async () => {
      const item = await this.table.get(itemId)
      if (!item) {
        throw new Error('Pack item not found')
      }

      const updates: Partial<PackItem> = {
        packed: !item.packed,
      }

      if (!item.packed) {
        // If packing the item, set packed timestamp
        updates.packedAt = new Date().toISOString()
      } else {
        // If unpacking, clear timestamp
        updates.packedAt = undefined
      }

      return this.update(itemId, updates).then(result => result.data!)
    }, 'toggle item packed')
  }

  /**
   * Assign item to user
   */
  async assignItem(itemId: string, userId: string): Promise<StorageResult<PackItem>> {
    return this.update(itemId, { assignedTo: userId })
  }

  /**
   * Unassign item
   */
  async unassignItem(itemId: string): Promise<StorageResult<PackItem>> {
    return this.update(itemId, { assignedTo: undefined })
  }

  /**
   * Bulk update item assignments
   */
  async bulkAssignItems(itemIds: string[], userId: string): Promise<StorageResult<PackItem[]>> {
    return this.executeOperation(async () => {
      const updatedItems: PackItem[] = []

      for (const itemId of itemIds) {
        const result = await this.assignItem(itemId, userId)
        if (result.success && result.data) {
          updatedItems.push(result.data)
        }
      }

      return updatedItems
    }, 'bulk assign items')
  }

  /**
   * Bulk toggle packed status
   */
  async bulkTogglePacked(itemIds: string[], packed: boolean): Promise<StorageResult<PackItem[]>> {
    return this.executeOperation(async () => {
      const updatedItems: PackItem[] = []
      const now = new Date().toISOString()

      for (const itemId of itemIds) {
        const updates: Partial<PackItem> = { packed }
        if (packed) {
          updates.packedAt = now
        } else {
          updates.packedAt = undefined
        }

        const result = await this.update(itemId, updates)
        if (result.success && result.data) {
          updatedItems.push(result.data)
        }
      }

      return updatedItems
    }, 'bulk toggle packed')
  }

  /**
   * Get items assigned to user
   */
  async getItemsAssignedToUser(userId: string): Promise<StorageResult<PackItem[]>> {
    return this.executeOperation(
      () => this.table.where('assignedTo').equals(userId).toArray(),
      'get items assigned to user'
    )
  }

  /**
   * Get item categories for pack list
   */
  async getItemCategories(packListId: string): Promise<StorageResult<string[]>> {
    return this.executeOperation(async () => {
      const items = await this.table.where('packListId').equals(packListId).toArray()
      const categories = [...new Set(items.map(item => item.category))]
      return categories.sort()
    }, 'get item categories')
  }

  /**
   * Calculate total weight for user's assigned items
   */
  async calculateUserWeight(packListId: string, userId: string): Promise<StorageResult<number>> {
    return this.executeOperation(async () => {
      const items = await this.table
        .where('packListId')
        .equals(packListId)
        .and(item => item.assignedTo === userId)
        .toArray()

      return items.reduce((total, item) => {
        return total + (item.weight ?? 0) * item.quantity
      }, 0)
    }, 'calculate user weight')
  }

  /**
   * Get packing statistics
   */
  async getPackingStats(packListId: string): Promise<
    StorageResult<{
      totalItems: number
      packedItems: number
      totalWeight: number
      packedWeight: number
      completionPercentage: number
      essentialItems: {
        total: number
        packed: number
      }
    }>
  > {
    return this.executeOperation(async () => {
      const items = await this.table.where('packListId').equals(packListId).toArray()

      const totalItems = items.length
      const packedItems = items.filter(item => item.packed).length
      const totalWeight = items.reduce((sum, item) => sum + (item.weight ?? 0) * item.quantity, 0)
      const packedWeight = items
        .filter(item => item.packed)
        .reduce((sum, item) => sum + (item.weight ?? 0) * item.quantity, 0)

      const essentialItems = {
        total: items.filter(item => item.isEssential).length,
        packed: items.filter(item => item.isEssential && item.packed).length,
      }

      const completionPercentage = totalItems > 0 ? (packedItems / totalItems) * 100 : 0

      return {
        totalItems,
        packedItems,
        totalWeight,
        packedWeight,
        completionPercentage,
        essentialItems,
      }
    }, 'get packing stats')
  }
}

// Create and export singleton instances
export const packListStorage = new PackListStorageService()
export const packItemStorage = new PackItemStorageService()
